import Handlebars from "utilities/vendor/handlebars/4.7.7/js/handlebars";
import hbs from "utilities/vendor/handlebars/4.7.7/js/handlebars";
import spartaEventsUtility from "utilities/global/sparta-events-utility/2.3.5/js/sparta-events-utility";
import spartaGlobalFunctions from "utilities/global/sparta-global-functions-utility/2.1.3/js/sparta-global-functions-utility";
import spartaStoreUtility from "utilities/global/sparta-store-utility/3.1.1/js/sparta-store-utility";
import jQuery from "utilities/vendor/jquery/3.5.1/js/jquery";
import $ from "utilities/vendor/jquery/3.5.1/js/jquery";
import _ from "utilities/vendor/lodash/4.17.21/js/lodash.custom.min";
import spartaBootstrapUtility from "utilities/global/sparta-bootstrap-utility/4.2.6/js/sparta-bootstrap-utility";
import spartaTransportUtility from "utilities/global/sparta-transport-utility/4.1.0/js/sparta-transport-utility";
import spartaMessageUtility from "utilities/global/sparta-messaging-utility/2.1.7/js/sparta-messaging-utility";
import spartaRequireUtility from "utilities/global/sparta-require-utility/2.3.5/js/sparta-require-utility";
import spartaExperienceUtility from "utilities/global/sparta-experience-utility/2.4.2/js/sparta-experience-utility";
import moduleContainer from "utilities/helpers/module-container/3.3.4/js/module-container";
import * as jcrUtility from "utilities/global/jcr-utility/1.2.3/js/jcr-utility";
import Communicator from "utilities/global/sparta-module-utility/6.2.6/js/communicator";
import Controller from "utilities/global/sparta-module-utility/6.2.6/js/controller";
import Model from "utilities/global/sparta-module-utility/6.2.6/js/model";
import View from "utilities/global/sparta-module-utility/6.2.6/js/view";
// Register the module-container helper
moduleContainer.register(Handlebars);

/**
 * Loads and waits for all given Handlebars helpers to load
 * @param {Array} helpers Array of string URLs to call require.js with
 * @returns {Promise} Promise that resolves when all helpers have been loaded
 */
function loadOnDemandHelpers(helpers) {
    return Promise.all(helpers.map((helper) => spartaRequireUtility.getRequirePromise([helper]))).then((helperExports) => {
        helperExports.forEach((helperExport) => helperExport.register(Handlebars));
    });
}

/**
 * To get helpers dependencies from given list of dependencies.
 * @param {Array<string>} depArray Array of dependencies.
 * @returns {Array<string>} Array of helper dependencies.
 */
function getHelpersDependencies(depArray = []) {
    const helpers = depArray.filter(({ id }) => id.match(/utilities\/helpers\/(.+?)\/(\d+\.\d+\.\d+(?:-[a-zA-Z0-9-]+)?)\/js\//));
    return helpers.map(({ id }) => id);
}

/**
 * Given a module reference and version, return the path to it's template file.
 * @param {string} ref The module's reference (e.g. 'global/global-nav-module').
 * @param {string} version The module version.
 * @returns {string} The module's template path.
 */
const getTemplatesPath = (ref, version) => `modules/${ref}/${version}/js/templates`;

/**
 * Given basic module information, retrieve more information about it,
 * such as default context, params, version, etc.
 * @param {Object} moduleObj Module information.
 * @returns {Object} Complete, validated module information.
 */
function getCompleteModuleInfo(moduleObj, onDemandModules) {
    let moduleInfo;

    if (moduleObj.mainJs && moduleObj.helpers) {
        moduleInfo = moduleObj;
    }
    else {
        const allModuleVersions = onDemandModules.modules[moduleObj.module];

        if (!allModuleVersions) throw new Error(`Couldn't find the on-demand module ${moduleObj.module}.`);

        // If user gives a version, find the module by the version passed in.
        // Otherwise, use the latest.
        // Either way, create a deep copy of the global value so loading the same module
        // multiple times doesn't reuse existing data.
        moduleInfo = _.cloneDeep(allModuleVersions.find(obj => obj.version === moduleObj.version) || allModuleVersions[0]);
    }

    // If no default context exists, create an empty one
    moduleInfo.context = moduleInfo.context || {};

    // If user doesn't pass in a context, use an empty object
    moduleObj.context = moduleObj.context || {};

    // Merge any params passed in.  Also, ensure the config is spread into params (for legacy sake)
    Object.assign(moduleInfo.context.params, moduleObj.context.params, moduleInfo.context.config);

    return {
        moduleRef: moduleObj.module,
        ...moduleInfo
    };
}

/**
 * Transforms a list of custom dataSources with URLs
 * into a list of fully resolved ones.
 *
 * For example:
 * {
 *   data1: 'sparta-demo/spa-assets/somefile.json'
 *   data2: {
 *     blah: 'blah'
 *   }
 * }
 *
 * gets transformed to:
 *
 * {
 *   data1: {
 *     someKey: 'someValue'
 *   },
 *   data2: {
 *     blah: 'blah'
 *   }
 * }
 *
 * @param {Object} customDataSources Custom dataSources
 * @param {Object} defaultDataSources Default dataSources, based on the site/page
 * @returns {Object} Fully resolved dataSources
 */
async function resolveModuleDataSources(customDataSources, defaultDataSources, spaParams) {
    const stringDataSources = _.pickBy(customDataSources, _.isString);
    const contextRootOrSiteName = _.get(spaParams.siteParams, 'contextRoot', spaParams.siteName);
    const jcrManifestDataSources = _.isEmpty(stringDataSources) ? stringDataSources : await jcrUtility.resolveDataSources(contextRootOrSiteName, stringDataSources);
    const resolvedDataSources = {};

    if(!_.isEmpty(jcrManifestDataSources)) {
        await Promise.all(
            _.map(jcrManifestDataSources, (url, key) => new Promise((resolve, reject) => {
                    spartaTransportUtility.subscribe({
                        cache: true,
                        dataType: 'json',
                        success: data => {
                            resolvedDataSources[key] = data;
                            resolve();
                        },
                        error: error => {
                            console.warn(`Unable to load requested datasource:${{ [key]: url }} and will fallback to default datasources`, error);
                            // Still resolve so that we can use any
                            // default data sources, if applicable.
                            resolve();
                        },
                        url
                    });
                })
            )
        );
    }
    
    return mapDataSourcesByLanguage({
            ...defaultDataSources,
            ..._.pickBy(customDataSources, _.isObject),
            ...resolvedDataSources,
    });
}

/**
 * Transforms a list of dataSources into a list by language.
 * This is needed so that the module-container helper is satisfied.
 *
 * Example of transformation:
 *
 * {
 *   'data1': {
 *     foo: 'bar'
 *   },
 *   'data2': {
 *     blah: 'blah'
 *   }
 * }
 *
 * gets transformed to:
 *
 * {
 *   data1: {
 *     en: {
 *       foo: 'bar'
 *     }
 *   },
 *   data2: {
 *     en: {
 *       blah: 'blah'
 *     }
 *   }
 * }
 * @param {Object} dataSources List of dataSources.
 * @returns {Object} List of dataSources, by language.
 */
function mapDataSourcesByLanguage(dataSources) {
    const mappedDataSources = {};

    _.each(dataSources, (dataSource, key) => {
        const source = {};
        source[window.sparta.language] = dataSource;

        mappedDataSources[key] = source;
    });

    return mappedDataSources;
}

/**
 * AJAX retrieve the module's template
 * @param {string} module The module's name (e.g. 'sparta-demo/global-nav-module-demo')
 * @param {string} version The module's version (e.g. '1.0.0')
 * @returns {Promise}
 */
function getModuleTemplate(module, version) {
    return spartaRequireUtility.getRequirePromise([getTemplatesPath(module, version)]);
}

/**
 * Register all module template with Handlebars
 * @param {object} moduleInit An object containing the module to initialize
 */
function registerModuleTemplates(moduleInit) {
    (moduleInit.default.templates || []).forEach(templateObj => {
        Handlebars.registerPartial(templateObj.name, Handlebars.template(templateObj.precompiled));
    });
}

/**
 * Retrieves and registers the module's template with Handlebars
 * @param {string} moduleRef The module's ref (e.g. 'sparta-demo/global-nav-module-demo')
 * @param {Object} moduleInit An object containing the module to initialize
 * @returns {Promise}
 */
function getAndRegisterModuleTemplates(moduleRef, version) {
    return getModuleTemplate(moduleRef, version).then(moduleInit => {
        registerModuleTemplates(moduleInit);
    });
}

/**
 * AJAX's the module's main JS if it hasn't already been defined
 * or retrieves its contents otherwise.
 * @param {string} mainPath The module's full reference (e.g. 'modules/<module-name>/x.x.x/js/global-nav-module-demo')
 * @returns {Promise} Promise that resolves when the mainJs file is loaded
 */
function getModuleMainJs(mainJs) {
    return spartaRequireUtility.getResourceWithDependencies(mainJs).catch(e => {
        throw new Error(`Error fetching module ${mainJs}: ${e.message}.`)
    });
}

/**
 * Validate and retrieve the target element
 * @param {string|HTMLElement} target Target element
 * @returns {HTMLElement} Validated target element
 */
function getMountElement(target) {
    const element = target instanceof HTMLElement
        ? target
        : document.querySelector(target);

    if (!(element instanceof HTMLElement)) {
        throw new Error(`Target ${target} isn't a valid HTML element or query selector string.`);
    }

    return element;
}

/**
 * Verify and create a new module Id if required
 * @param {string} module Id
 * @returns {string} same or new module Id
 */
function getModuleUniqueId(moduleId) {
    if (!document.getElementById(moduleId)) {
        return moduleId;
    }

    let moduleCount = 0;
    while(document.getElementById(moduleId + (++moduleCount)));

    return moduleId + moduleCount;
}

export default class Module {
    constructor(options) {

        let defaults = {
                'target': null,
                '$target': $(),
                'debug': false,
                'events': {
                    'namespace': 'module'
                }
            },
            required = {};

        this.settings = $.extend(true, {}, defaults, options, required);
        this.settings.target = this.settings.class ? this.settings.class : this.settings.target;
        this.settings.$target = $(this.settings.target);
        this.settings.SPARTA_READY = (this.settings.$target.data('sparta-load') === 'primary') ? 'SPARTA_GLOBAL_READY.spartaBootstrapUtility' : 'SPARTA_SECONDARY_READY.spartaBootstrapUtility';

        /*  Gather all appropriate data attributes associated with this module.
            These can then be accessed as overrides on default actions/behaviors/values.
        */
        for ( data in this.settings.data ) {
            if ( this.settings.$target.data( data ) ) {
                this.settings.data[ data ] = this.settings.$target.data( data );
            }
        }

        this.model = new Model({ settings: this.settings });
        this.view = new View({ model: this.model, settings: this.settings });
        this.controller = new Controller({ model: this.model, settings: this.settings });

        this.Events = spartaEventsUtility;
        this.Store =  spartaStoreUtility;
        this.Transport = spartaTransportUtility;
        this.SpartaFunctions = spartaGlobalFunctions;
        this.Experience = spartaExperienceUtility;
        this.Message = spartaMessageUtility;

        this.applicationContext = spartaStoreUtility.getData('applicationContext');
        this.applicationContainer = spartaStoreUtility.getData('applicationContainer');
        this.spaParams = spartaStoreUtility.getData('spaParams');
        this.eventDelegatorString = (this.applicationContext) ? `[data-version='${this.applicationContext.version}'][data-sparta-container='${this.applicationContext.name}']` : '[data-sparta-container]';
        this.isWidget = !!spartaStoreUtility.getData('isWidget');

        this.params = this.settings.$target.data('module-parameters');
        
        if (this.isWidget && this.applicationContext.id) {
            this.spaParams = this.spaParams[this.applicationContext.id];
        }        

        // sets module info (name/version/options)
        this.settings.moduleInfo = {
            name: this.settings.$target.data('module'),
            version: this.settings.$target.data('version')
        }

        // options are set on applicationContainer in 'data-sparta-overrides' via Bootstrap-utility
        this.settings.moduleInfo.options = this.Store.getData('applicationOptions')[this.settings.moduleInfo.name];

        const onDemandModules = this.spaParams.onDemandModules || {};
        this.baseHelpersPromise = onDemandModules && onDemandModules.baseHelpers ?
            loadOnDemandHelpers(onDemandModules.baseHelpers) :
            undefined;

        // this.Store.register( this );
        spartaBootstrapUtility.register(this);
    }

    /**
     * Mounts a set of module objects (or single object) to the given selectors/elements.
     *
     * Schema:
     * (required) module: reference of the module to mount
     * (required) target: string (or HTMLElement) of the element to mount the module to
     * (optional) context: object of dataSources and/or params to compile the module with.
     * dataSource values can either be relative-pathed URLs or objects, and any URLs provided will be
     * fully resolved as objects.
     * params values must be fully resolved.
     *
     * Advanced:
     * (optional) version: hardcoded version of the module to load, such as '1.0.0'.
     *
     *
     * Usage #1: module is an Array of modules to mount
     * this.mountAll([{
     *   module: 'sparta-demo/global-nav-module',
     *   target: '#some-selector',
     *   context: {
     *     dataSources: {
     *       data1: '/sparta-demo/some/path/file.json'
     *     },
     *     params: {
     *       foo: 'bar'
     *     }
     *   }
     * }]);
     *
     * Usage #2: module is a single object that represents a single module to mount
     * this.mountAll({
     *   module: 'sparta-demo/global-nav-module',
     *   target: '#some-selector',
     *   context: {
     *     dataSources: {
     *       data1: '/sparta-demo/some/path/file.json'
     *     },
     *     params: {
     *       foo: 'bar'
     *     }
     *   }
     * });
     *
     * @param {Array|Object} module An array (or single object) of modules to load.
     * @memberof Module
     * @returns {Promise}
     */
    mountAll(modules) {
        if (this.baseHelpersPromise) {
            return this.baseHelpersPromise.then(async () => {
                const moduleArray = Array.isArray(modules) ? modules : [modules];
                const modulesMetaData = await this._loadModulesWithMetaData(moduleArray);
                return Promise.all(modulesMetaData.map(this._mount.bind(this)));
            });
        }
        return Promise.reject(new Error('No on-demand modules found.'));
    }

    /**
     * Load Modules and its meta data information.
     * @param {Array<Object>} modules Module basic information.
     * @returns {Promise} Array of module meta data information.
     * @example
     * {
     * { 
     *   moduleMetaData: { moduleRef, helpers, mainJs, version, context }, // Meta data information.
     *   loadedModule, // Module instance loaded using requirejs.
     *   moduleObj  // Module basic information received.
     *  }
     * }
     */
    async _loadModulesWithMetaData(modules) {
        const modulesInfo = _.map(modules, module => ({
            moduleObj: module,
            moduleMetaData: getCompleteModuleInfo(module, this.spaParams.onDemandModules),
        }));
        const uniqueModulesMainJs = _.uniqBy(modulesInfo, 'moduleMetaData.mainJs');
        const modulesLoaded = await Promise.all(_.map(uniqueModulesMainJs, ({ moduleMetaData: { mainJs } }) => getModuleMainJs(mainJs)))

        return _.map(modulesInfo, moduleInfo => {
            return {
                ...moduleInfo,
                loadedModule: _.find(modulesLoaded, { map: { id: moduleInfo.moduleMetaData.mainJs } })
            }
        });
    }


    /**
     * Mounts a module to the given selector (or element).
     * This is accomplished by a series of steps: 
     * 1. AJAX load and register any module Handlebars helpers (such as helper-json).
     * 2. AJAX load all module templates and register each template partial.
     * 3. AJAX load the module's main JS file (such as global-nav-module.js), which
     *    includes any dependencies.
     * 4. Generate the module's HTML using the module-component helper.
     * 5. Attach the module's HTML to the given element.
     * 6. Initialize the module using spartaBootstrapUtility.
     * @param {Object} module An object containing module information.
     * @returns {Promise} Mounts a single module and return Module information of mounted module.
     */
    async _mount(module) {
        const { 
            moduleMetaData: { moduleRef, helpers, mainJs, version, context },
            loadedModule, 
            moduleObj 
        } = module;

        const { context: mainModuleContext, map: { id }, depArray } = loadedModule;
        const mainJsInit = mainModuleContext.defined[id];
        const moduleDependencyHelpers = getHelpersDependencies(depArray);  
        const allHelpers = [ ...moduleDependencyHelpers, ...helpers ];

        await Promise.all([
            resolveModuleDataSources(moduleObj.context.dataSources, context.dataSources, this.spaParams).then((dataSources) => {
                context.dataSources = dataSources;
            }),
            loadOnDemandHelpers(allHelpers),
            getAndRegisterModuleTemplates(moduleRef, version),
        ]);

        const element = getMountElement(moduleObj.target);

        context.config.id = getModuleUniqueId(context.config.id);

        element.innerHTML = moduleContainer.helpers.composeModule({
                    global: this.spaParams.global,
                    params: this.spaParams.siteParams
                }, {
                    parameters: this.spaParams.pageParams
                },
                window.sparta.language,
                window.sparta.env,
                undefined,
                context,
            );
        spartaBootstrapUtility.createAndInitModules([mainJsInit]);
        return moduleObj;
    }

    _debug(msg) {
        if ( window.console && this.settings.debug ) {
            console.log( msg );
        }
    };

    static mountAll(moduleObj) {
        const element = moduleObj.target instanceof HTMLElement
            ? moduleObj.target
            : document.querySelector(moduleObj.target);

        const FakeMod = class FakeModule extends Module {
            constructor(options) {
                super(options);
            }

            init(args = {}) {}
        }

        const fakeModuleInstance = new FakeMod({
            target: element
        });


        if (fakeModuleInstance.baseHelpersPromise) {
            return fakeModuleInstance.baseHelpersPromise.then(() => {
                if (Array.isArray(moduleObj)) {
                    return Promise.all(moduleObj.map(fakeModuleInstance.mountAll));
                }

                return fakeModuleInstance.mountAll(moduleObj);
            });
        }

        return Promise.reject(new Error('No on-demand modules found.'));
    }

};
